import React from "react";
import {
  alpha,
  Badge,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import MapIcon from "@mui/icons-material/Map";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";

export default function DashboardTab({
  alignment,
  handleAlignment,
  viewMode,
  onViewModeChange,
  mapViewState,
  onMapViewStateChange,
  onShowMapPreview,
}) {
  const theme = useTheme();

  // Redux selectors
  const preparingOrdersCount = useSelector(
    (state) => state.orders.preparingOrdersCount
  );
const activeShippingRoutesCount = useSelector(
    (state) => state.orders.activeShippingRoutesCount
  );
  const smartRoutingCount = useSelector(
    (state) => state.smartRouting.data?.data?.routes?.length || 0
  );

  const dashboardItems = [
    {
      value: "orders",
      label: "Em Preparo",
      count: preparingOrdersCount,
      icon: RestaurantIcon,
      color: theme.palette.error.main,
    },
{
      value: "drivers-delivering",
      label: "Na Rua",
      count: activeShippingRoutesCount,
      icon: DirectionsCarIcon,
      color: theme.palette.success.main,
    },
    {
      value: "smart-routes",
      label: "Rotas",
      count: smartRoutingCount,
      icon: AltRouteIcon,
      color: theme.palette.warning.main,
    },
  ];

  const TabButton = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    
    return (
      <ButtonBase
        onClick={onClick}
        sx={{
          borderRadius: "6px",
          px: 3,
          py: 1.2,
          position: "relative",
          zIndex: 1,
          transition: "color 0.2s ease-in-out",
          // Color logic handled below to ensure contrast
          color: isActive ? theme.palette.getContrastText(alpha(item.color, 0.15)) : theme.palette.text.secondary,
          "&:hover": {
            color: isActive ? undefined : theme.palette.text.primary,
          },
        }}
      >
        {isActive && (
          <Box
            component={motion.div}
            layoutId="activeTabPill"
            initial={false}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "6px",
              bgcolor: alpha(item.color, 0.12),
              zIndex: -1,
            }}
          />
        )}
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Badge
                badgeContent={item.count || 0}
                color="error"
                invisible={!item.count}
                sx={{
                    "& .MuiBadge-badge": {
                        bgcolor: isActive ? item.color : theme.palette.grey[500],
                        color: "white",
                        fontWeight: "bold",
                        border: `2px solid ${theme.palette.background.paper}`,
                        transition: "background-color 0.3s ease",
                    }
                }}
            >
                <Icon sx={{ 
                    fontSize: 24,
                    color: isActive ? item.color : "inherit"
                }} />
            </Badge>
            <Box 
              component="span" 
              sx={{ 
                display: { xs: "none", md: "block" }, 
                fontWeight: isActive ? 700 : 500,
                fontSize: "0.95rem",
                color: isActive ? item.color : "inherit",
              }}
            >
                {item.label}
            </Box>
        </Box>
      </ButtonBase>
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Navigation Tabs */}
      <Box sx={{ display: "flex", gap: 1 }}>
        {dashboardItems.map((item) => (
          <TabButton
            key={item.value}
            item={item}
            isActive={alignment === item.value}
            onClick={() => handleAlignment(null, item.value)}
          />
        ))}
      </Box>

      {/* Small vertical divider inside the tab group */}
      <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: "center", mx: 2, borderColor: theme.palette.divider }} />

      {/* Contextual Controls (View Mode & Map Toggle) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
         <Tooltip title={viewMode === "list" ? "Lista" : "Grade"}>
            <IconButton
                onClick={() => onViewModeChange(viewMode === "list" ? "grid" : "list")}
                sx={{
                    color: theme.palette.text.secondary,
                    bgcolor: alpha(theme.palette.text.primary, 0.03),
                    borderRadius: "6px",
                    p: 1,
                    "&:hover": { color: theme.palette.text.primary, bgcolor: alpha(theme.palette.text.primary, 0.08) }
                }}
            >
                {viewMode === "list" ? <ViewListIcon /> : <GridViewIcon />}
            </IconButton>
        </Tooltip>
        
        <Tooltip title={mapViewState === "hidden" ? "Mostrar Mapa" : "Ocultar Mapa"}>
            <IconButton
                onClick={onMapViewStateChange}
                onMouseEnter={mapViewState === "hidden" ? onShowMapPreview : undefined}
                sx={{
                   color: mapViewState === "map" ? "white" : theme.palette.text.secondary,
                   bgcolor: mapViewState === "map" ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.03),
                   borderRadius: "12px",
                   p: 1,
                   boxShadow: mapViewState === "map" ? theme.shadows[4] : "none",
                   "&:hover": { 
                       bgcolor: mapViewState === "map" ? theme.palette.primary.dark : alpha(theme.palette.text.primary, 0.08) 
                   }
                }}
            >
                {mapViewState === "hidden" ? <VisibilityOffIcon /> : <MapIcon />}
            </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
